
import React from 'react';

const AirMonitoringSection: React.FC = () => {
  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Air Monitoring Services
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Air monitoring, a vital service of industrial hygiene, involves the detection and quantification of airborne hazards, including welding fumes, hexavalent chromium, silica, respirable dust, and volatile organic compounds (VOCs) emanating from paint booths.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Setting Up an Air Monitoring Study
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                We design an air monitoring study to meet each organization's unique requirements. It begins by evaluating the chemicals used in the facility and the types of particulate matter employees may be exposed to. We work with clients to understand their manufacturing processes, the activities performed (i.e., welding, painting, cutting, grinding), and the facility's layout.
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed">
                We review safety data sheets to identify the chemicals requiring monitoring and to understand where respiratory protection may be necessary. These sheets also detail the permissible exposure limits, guiding the monitoring process to ensure compliance with established safety standards.
              </p>
              
              <div className="bg-blue-50 p-6 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">
                  Common Airborne Hazards We Monitor:
                </h4>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="flex items-center">
                    <i className="ri-alert-line text-orange-500 mr-2"></i>
                    <span className="text-gray-700">Welding Fumes</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-alert-line text-orange-500 mr-2"></i>
                    <span className="text-gray-700">Hexavalent Chromium</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-alert-line text-orange-500 mr-2"></i>
                    <span className="text-gray-700">Crystalline Silica</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-alert-line text-orange-500 mr-2"></i>
                    <span className="text-gray-700">Respirable Dust</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-alert-line text-orange-500 mr-2"></i>
                    <span className="text-gray-700">VOCs from Paint</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-alert-line text-orange-500 mr-2"></i>
                    <span className="text-gray-700">Chemical Vapors</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                From Monitoring to Corrective Action
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                The data collected is analyzed post-monitoring, and we generate a comprehensive report. This report outlines the findings for each individual and area monitored, offering recommendations based on the levels of exposure observed.
              </p>
              <p className="text-gray-700 mb-8 leading-relaxed">
                Should the results indicate exposure at or above the action level or permissible exposure limit, tailored advice is provided on mitigating the risks to employees, from implementing engineering controls to adjusting work practices and using personal protective equipment (PPE).
              </p>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-search-line text-blue-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Initial Assessment</h4>
                    <p className="text-gray-700">Comprehensive facility evaluation and hazard identification</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-test-tube-line text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">On-Site Monitoring</h4>
                    <p className="text-gray-700">Professional air sampling using calibrated equipment</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-file-chart-line text-purple-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Detailed Reporting</h4>
                    <p className="text-gray-700">Comprehensive analysis with actionable recommendations</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AirMonitoringSection;
